import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnChanges,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { formatDistanceToNowStrict } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { interval, Subject, takeUntil } from 'rxjs';

import { EventAttendanceStatus } from '@modules/section/models/attendance.model';
import { EventModel, EventStates } from '@modules/section/models/event.model';
import { Locale } from 'src/environments/language.config';
import { isContentOutdated } from 'src/helpers/dates';

import { MediaComponent } from '../../media/media.component';
import { LocalizedDatePipe } from '../../pipes/localized-date.pipe';
import { TimePeriodPipe } from '../../pipes/time-period.pipe';
import { LiveIndicatorComponent } from '../../ui/live-indicator/live-indicator.component';
import { SmallSpinnerComponent } from '../../ui/small-spinner/small-spinner.component';

@Component({
  selector: 'mb-event-ribbon-card',
  templateUrl: './event-ribbon-card.component.html',
  styleUrls: ['./event-ribbon-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    RouterLink,
    MediaComponent,
    SmallSpinnerComponent,
    LiveIndicatorComponent,
    LocalizedDatePipe,
    TimePeriodPipe,
    TranslatePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EventRibbonCardComponent implements OnChanges, OnDestroy {
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  public attendanceIcons: any = {
    representative: 'representative',
    has_substitute: 'representative',
    pending: 'invited',
    open: 'invited',
    confirmed: 'accepted',
    canceled: 'declined',
    declined: 'declined',
  };

  // used thumbor path

  @Input() event!: EventModel;

  // displayed counter maxEvent / countEvent
  @Input()
  index!: number;
  @Input()
  total!: number;

  public distance?: string;
  public attendanceIcon!: string;
  public attendance_status = EventAttendanceStatus;
  public eventStates = EventStates;

  private destroy$ = new Subject<boolean>();

  ngOnChanges() {
    if (!this.event) {
      return;
    }

    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.event || this.event.state !== EventStates.UPCOMING) {
          this.destroy$.next(true);
          return;
        }

        this.distance = formatDistanceToNowStrict(this.event.startDate, {
          addSuffix: true,
          locale: this.translateService.currentLang() === Locale.DE ? de : enUS, // TODO: integrate this into app service so we can use this across the app
        });
        this.cdr.detectChanges();
      });
  }

  // a long event can have started over a year ago and still be running - never label it archived then
  public get isArchived(): boolean {
    return this.event.state !== EventStates.RUNNING && isContentOutdated(this.event.startDate);
  }

  public isExternalUrl() {
    return this.event.urlSlug?.startsWith('https://');
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
